import React from "react";
import {
  Box,
  Typography,
  Container,
  useMediaQuery,
  styled,
} from "@mui/material";
import { motion } from "framer-motion";
import { AnimatedButton } from "../../ui/AnimatedButton";

const skillIcons = [
  "/images/Figma.png",
  "/images/Frammer.png",
  "/images/Git.png",
  "/images/JS.png",
  "/images/MUI.png",
  "/images/Photoshop.png",
  "/images/React.png",
  "/images/TS.png",
  "/images/Tailwind.png",
  "/images/ShadCN.png",
  "/images/NextJS.png",
];

const IconContainer = styled(Box)(({ theme }) => ({ 
  display: "flex",
  position: "absolute",
  bottom: 0,
  left: 0,
  width: "100%",
  justifyContent: "center",
  paddingBottom: "10px",
  maskImage:
    "linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgb(0, 0, 0) 50%, rgb(0, 0, 0) 50%, rgba(0, 0, 0, 0) 100%)",
}));

const IconWrapper = styled(motion.div)({
  display: "flex",
  alignItems: "center",
});

const Icon = styled("img")({
  width: "40px",
  height: "40px",
  margin: "0 10px",
});

const StyledBadge = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(55, 212, 133, 0.8)",
  color: "rgba(55, 212, 133, 0.8)",
  width: 10,
  height: 10,
  borderRadius: "50%",
  position: "relative",
  "&::after": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    border: "1px solid currentColor",
    content: '""',
    animation: "ripple 1.2s infinite ease-in-out",
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(0.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

const Hero: React.FC = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isXMobile = useMediaQuery("(max-width:400px)");

  const getHeadingLevel = isXMobile ? "h5" : isMobile ? "h4" : "h3";

  const extendedIcons = Array.from({ length: 10 }, () => skillIcons).flat();

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      style={{ textAlign: "center" }}
    >
      <Container
        maxWidth={"xs"}
        sx={{ position: "relative", overflow: "hidden", mb: 2 }}
      >
        <Box
          sx={{
            width: isMobile ? 250 : 300,
            height: isMobile ? 250 : 300,
            borderRadius: "50%",
            bgcolor: "rgb(21, 21, 24)",
            margin: "0 auto",
            mb: 4,
            position: "relative",
          }}
        >
          <Box
            component="img"
            src="../images/Abdulrahman.jpg"
            alt="Silhouette"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "50%",
              padding: "8px",
              maskImage:
                "linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgb(0, 0, 0) 50%, rgb(0, 0, 0) 50%, rgba(0, 0, 0, 0) 100%)",
            }}
          />
        </Box>

        <IconContainer>
          <IconWrapper
            initial={{ x: "0%" }}
            animate={{ x: "-100%" }}
            transition={{
              duration: 30,
              ease: "linear",
              repeat: Infinity,
            }}
            whileHover={{
              x: "-100%",
              transition: { duration: 40, ease: "linear", repeat: Infinity },
            }}
          >
            {extendedIcons.map((icon, index) => (
              <Icon key={index} src={icon} alt={`Skill icon ${index + 1}`} />
            ))}
          </IconWrapper>
        </IconContainer>
      </Container>

      <Typography variant={getHeadingLevel} sx={{ maxWidth: 650, margin: "0 auto"}}>
        Hi, I'm Abdulrahman 👋 I develop <b>Responsive web apps</b> and create seamless {""}
        <span
          style={{
            fontFamily: "'Modure Placeholder', serif",
            fontStyle: "italic",
            display: "inline",
          }}
        >
          UI/UX experiences.
        </span>
      </Typography>

      <AnimatedButton
        backgroundColor="#0d0d0d"
        textColor="rgb(155, 199, 177)"
        gradientColor="rgb(49, 177, 112)"
        glowStart="rgb(144, 238, 144)"
        glowEnd="rgb(34, 139, 34)"
        borderRadius="0.75rem"
        sx={{ height: 40, mt: 2, cursor: "inherit" }}
      >
        <StyledBadge sx={{mr: 1}}/> Open to Work
      </AnimatedButton>
    </motion.div>
  );
};

export default Hero;
